import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useGridCells, useLocalState, useTranslation } from 'hooks';
import { OrganisationMemberFields } from 'queries/generated/fragments.generated';
import { GET_RESTRICTED_STUDY } from 'queries/studies';
import { GET_ORGANISATION } from 'queries/uam';
import ExpandableGrid from 'views/components/core/expandable/grid/index';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';

import './uam-styles.scss';

const StudyTeamGrid = () => {
    const { t } = useTranslation();
    const { state } = useLocalState();
    const { YesNoCell, MaxLengthCell, DateCell } = useGridCells();
    const [users, setUsers] = useState([]);
    const [study, setStudy] = useState(null);

    const [getItems, { loading, error, data }] = useLazyQuery(GET_ORGANISATION);
    const [loadStudyData, { loading: loadingStudy, data: studyData }] = useLazyQuery(GET_RESTRICTED_STUDY);

    useEffect(() => {
        const array: any[] = [];
        if (study && data?.organisation)
            study.members?.forEach((item: any) => {
                const user = data?.organisation?.members.find(
                    (el: OrganisationMemberFields) => el.userId === item.userId,
                );

                const newItem = {
                    ...item,
                    isOpraUser: !!item.user,
                    isAdmin: user?.isAdmin ?? false,
                    recordId: item.user?.recordId,
                };

                array.push(newItem);
            });
        const newData = restructuredUpdatedCreatedBy(array);

        setUsers(
            orderBy(newData, [
                { field: 'isOpraUser', dir: 'desc' },
                { field: 'active', dir: 'desc' },
                { field: 'lastName', dir: 'asc' },
            ]),
        );
    }, [study, data]);

    useEffect(() => {
        if (state?.selectedStudy)
            loadStudyData({ variables: { id: state.selectedStudy.id as string } }).catch((e) => console.error(e));
    }, [state?.selectedStudy]);

    useEffect(() => {
        if (studyData?.study) {
            setStudy(studyData?.study);
            getItems().catch((e) => console.error(e));
        } else {
            setStudy(null);
        }
    }, [studyData]);

    if (loading || loadingStudy) return <Loading />;

    if (error) return <Error error={error} />;

    return (
        <>
            <PageTitle text={t('studyTeam')} />
            <div style={{ marginBottom: 40 }} />
            <ExpandableGrid
                customKey="study-team-grid"
                id="studyTeamGrid"
                key="study-team-grid"
                items={users}
                rowActions={null}
                groupable={false}
                showPager
                scrollable="scrollable"
                reorderable
                pageable={false}
                showFirstColumn={false}
                addScroll
                resizable
                options={{
                    allowExcelExport: false,
                    allowExpandCollapseAll: false,
                    useFilterChips: true,
                }}
                detail={null}
                columns={[
                    {
                        label: t('active'),
                        id: 'active',
                        field: 'active',
                        minWidth: 140,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilter,
                        lockedByDefault: true,
                    },
                    {
                        label: t('opraUser'),
                        id: 'opra-user',
                        field: 'isOpraUser',
                        minWidth: 230,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilter,
                        lockedByDefault: true,
                    },
                    {
                        label: t('lastName'),
                        id: 'last-name',
                        field: 'lastName',
                        minWidth: 230,
                        lockedByDefault: true,
                        cells: { data: MaxLengthCell(50) },
                    },
                    {
                        label: t('firstName'),
                        id: 'first-name',
                        field: 'firstName',
                        minWidth: 230,
                        cells: { data: MaxLengthCell(50) },
                    },
                    {
                        label: t('email'),
                        id: 'email',
                        field: 'email',
                        minWidth: 170,
                    },
                    {
                        label: t('uam:studyAdministrator'),
                        id: 'administrator',
                        field: 'isAdmin',
                        minWidth: 250,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                    {
                        label: t('ra:createdDate'),
                        field: 'created',
                        id: 'study-member-created',
                        filter: 'date',
                        minWidth: 200,
                        cells: { data: DateCell },
                    },
                    {
                        label: t('ra:createdBy'),
                        field: '_createdByString',
                        id: 'study-member-createdby',
                        minWidth: 200,
                    },
                    {
                        label: t('ra:updatedDate'),
                        field: 'updated',
                        id: 'study-member-updated',
                        filter: 'date',
                        minWidth: 200,
                        cells: { data: DateCell },
                    },
                    {
                        label: t('ra:updatedBy'),
                        field: '_updatedByString',
                        id: 'study-member-updatedby',
                        minWidth: 200,
                    },
                    {
                        field: 'recordId',
                        showByDefault: false,
                        filter: 'numeric',
                        label: 'ID',
                        id: 'study-team-id',
                        minWidth: 230,
                    },
                ]}
            />
        </>
    );
};

export default StudyTeamGrid;
